import Phaser from 'phaser';
import { VOICE_LINES } from '../data/voiceManifest';
import { saveManager } from './SaveManager';

interface PlayOpts {
  category?: string;
  /** Minimum seconds between lines of the same category. */
  cooldown?: number;
  /** Interrupt a line that is already playing. */
  priority?: boolean;
  /** Bypass the chatty filter (used for setting acknowledgements). */
  force?: boolean;
  volume?: number;
}

/** Categories that still play when the player picks "warnings only". */
const WARNING_CATEGORIES = new Set([
  'breach',
  'neardeath',
  'integrity',
  'boss',
  'bossdown',
  'elitedown',
  'lastwave',
  'defeat',
  'victory',
  'resolution',
]);

/**
 * Plays the cached voice-over lines. Enforces one line at a time with a
 * per-category cooldown so callouts never turn into a wall of noise.
 */
class VoiceSystem {
  private current: Phaser.Sound.BaseSound | null = null;
  private lastAt: Record<string, number> = {};
  private lastId = '';
  /** Session counts for each authored phrase, used to balance line coverage. */
  private playCounts: Record<string, number> = {};

  private get enabled(): boolean {
    return saveManager.data.settings.voice;
  }

  private get level(): number {
    return saveManager.data.settings.voiceVolume;
  }

  play(scene: Phaser.Scene, id: string, opts: PlayOpts = {}): boolean {
    if (!this.enabled) return false;
    const line = VOICE_LINES.find((l) => l.id === id);
    if (!line) return false;
    const key = `voice-${id}`;
    if (!scene.cache.audio.exists(key)) return false;

    const cat = opts.category ?? line.category;
    const chatty = saveManager.data.settings.chatty;
    if (!opts.force) {
      if (chatty === 'off') return false;
      if (chatty === 'warnings' && !WARNING_CATEGORIES.has(cat)) return false;
    }

    const now = scene.time.now;
    const sparse = chatty === 'sparse' && !opts.force;
    const cooldown = (opts.cooldown ?? 5) * (sparse ? 2.5 : 1) * 1000;
    if ((this.lastAt[cat] ?? -1e9) > now - cooldown) return false;

    if (this.current && this.current.isPlaying) {
      if (!opts.priority) return false;
      this.current.stop();
    }

    const snd = scene.sound.add(key, { volume: opts.volume ?? this.level });
    snd.once(Phaser.Sound.Events.COMPLETE, () => snd.destroy());
    snd.play();
    this.current = snd;
    this.lastAt[cat] = now;
    this.lastId = id;
    this.playCounts[id] = (this.playCounts[id] ?? 0) + 1;
    return true;
  }

  playCategory(scene: Phaser.Scene, category: string, opts: PlayOpts = {}): boolean {
    const pool = VOICE_LINES.filter((l) => l.category === category);
    if (!pool.length) return false;

    // Always prefer the least-heard phrase in this category. Randomise only
    // equal-count ties so the rotation stays natural while neglected lines
    // are guaranteed to surface before frequently repeated ones.
    const minCount = Math.min(...pool.map((line) => this.playCounts[line.id] ?? 0));
    const options = pool.filter((line) => (this.playCounts[line.id] ?? 0) === minCount);
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }
    for (const pick of options) {
      if (this.play(scene, pick.id, { category, ...opts })) return true;
    }
    return false;
  }

  stop(): void {
    if (this.current?.isPlaying) this.current.stop();
    this.current = null;
  }
}

export const voice = new VoiceSystem();
